import { foldOutcomeKeyForCompare } from "@/utils/orgOutcomes/slugifyOrgOutcomeKey";

/**
 * Static default outcome slugs for the org-level outcomes library.
 * These are UI-owned (not fetched from Galactus). Galactus may also return
 * the same keys on GET /v1/org_outcomes — consumers should dedupe by key.
 *
 * Keep the set in sync with Galactus `OrgOutcome.SystemOutcomeDefinitions`.
 * Order matches historical redialing defaults to avoid UI reshuffles.
 */
export const DEFAULT_ORG_OUTCOME_SLUGS = [
  "busy",
  "no-answer",
  "voicemail",
  "failed",
  "too-short",
  "other",
  "do-not-call",
  "contact-threshold-exceeded",
  "no-user-response",
  "only-user-speaks",
] as const;

export type DefaultOrgOutcomeSlug = (typeof DEFAULT_ORG_OUTCOME_SLUGS)[number];

/**
 * English fallback definitions (Galactus system copy). Prefer i18n
 * `knowledge:outcomes.defaults.<slug>.definition` in UI.
 */
export const DEFAULT_ORG_OUTCOME_DEFINITIONS: Record<
  DefaultOrgOutcomeSlug,
  string
> = {
  busy: "The call did not connect because the phone was busy.",
  "no-answer":
    "The call was not answered and the call did not go to voicemail.",
  voicemail:
    "The call was not answered but was answered by a voicemail service.",
  failed: "The call did not connect due to some failure.",
  "too-short":
    "The call was too short to determine an appropriate outcome.",
  other:
    "The outcome of the call does not match any other outcome and cannot be determined. Use this as a last resort when no other outcome can reasonably be determined.",
  "do-not-call":
    "The callee explicitly states they do not want to be called again.",
  "contact-threshold-exceeded":
    "The call exceeded a configured contact attempt threshold.",
  "no-user-response": "No user response detected in the call.",
  "only-user-speaks": "Only user speaks in the call.",
};

const DEFAULT_ORG_OUTCOME_BY_FOLDED = new Map<string, DefaultOrgOutcomeSlug>(
  DEFAULT_ORG_OUTCOME_SLUGS.map((slug) => [foldOutcomeKeyForCompare(slug), slug])
);

/** Canonical const slug when `key` is a system default (case/separator insensitive). */
export function canonicalDefaultOrgOutcomeSlug(
  key: string
): DefaultOrgOutcomeSlug | undefined {
  const folded = foldOutcomeKeyForCompare(key);
  if (!folded) return undefined;
  return DEFAULT_ORG_OUTCOME_BY_FOLDED.get(folded);
}

export function isDefaultOrgOutcomeSlug(key: string): boolean {
  return canonicalDefaultOrgOutcomeSlug(key) !== undefined;
}
